import { Component, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { Store } from "@ngrx/store";
import {
  GlobalMessageService,
  GlobalMessageType,
  Product,
  TranslationService
} from "@spartacus/core";
import { Observable } from "rxjs";
import { filter, switchMap, tap } from "rxjs/operators";
import {
  CustomManufacturerCampaign,
  CustomManufacturerCampaignOverview,
  CustomManufacturerCampaignSortOptions,
  ManufacturerMapping
} from "../../custom-manufacturer-campaign.models";
import { CustomManufacturerCampaignService } from "../../custom-manufacturer-campaign.service";
import { getManufacturerCampaign } from "../../store/custom-manufacturer-campaign.actions";
import { CustomManufacturerCampaignState } from "../../store/custom-manufacturer-campaign.reducer";
import {
  errorManufacturerCampaignOverview,
  selectManufacturerCampaignOverview,
  updateSalesNotificationSuccess
} from "../../store/custom-manufacturer-campaign.selectors";

@Component({
    selector: "app-custom-manufacturer-campaign",
    templateUrl: "./custom-manufacturer-campaign.component.html",
    styleUrls: ["./custom-manufacturer-campaign.component.scss"],
    standalone: false
})
export class CustomManufacturerCampaignComponent implements OnInit {
  @ViewChild("searchForm") searchForm: NgForm;

  manufacturerCampaignOverview$: Observable<CustomManufacturerCampaignOverview> =
  this.store.select(selectManufacturerCampaignOverview);
  errorOverview$: Observable<Error> = this.store.select(errorManufacturerCampaignOverview);
  updateSuccess$: Observable<boolean> = this.store.select(updateSalesNotificationSuccess);

  overview: CustomManufacturerCampaignOverview;
  responses: CustomManufacturerCampaign[];
  manufacturerCampaignList: CustomManufacturerCampaign[];
  manufacturerMappingList: ManufacturerMapping[];
  errorOverview: Error;

  selectedSortOption: string;
  sortOptions: string[];
  sortDirection: string = "desc";
  compare = (v1: string | number | Date, v2: string | number | Date) =>
    v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

  productCode: string = '';
  manufacturerNumber: string = '';
  ean: string = '';
  manufacturerId = '';
  campaignId = '';
  showOldData = false;

  fileName = '';
  fileToBeImported: File;
  b2bUnitId: string;

  constructor(
    private store: Store<CustomManufacturerCampaignState>,
    private manufacturerCampaignService :CustomManufacturerCampaignService,
    private translation: TranslationService,
    private messageService: GlobalMessageService
  ) {}

  ngOnInit(): void {
    this.setSortOptions();
    this.getManufacturerCampaignOverview(this.productCode,this.manufacturerNumber,this.ean,this.manufacturerId,this.campaignId,this.showOldData);

    this.manufacturerCampaignOverview$
      .pipe(
        filter(
          (manufacturerCampaignOverview) => manufacturerCampaignOverview != null
        ),
        tap((manufacturerCampaignOverview:CustomManufacturerCampaignOverview) => {
          this.overview = manufacturerCampaignOverview;
          this.manufacturerCampaignList = manufacturerCampaignOverview.manufacturerCampaignList;
          this.manufacturerMappingList = manufacturerCampaignOverview.manufacturerMappingList;
          this.responses = [
            ...manufacturerCampaignOverview.responses,
          ];
          this.b2bUnitId = manufacturerCampaignOverview.b2bUnitId;
          this.onChangeSort();
        })
      )
      .subscribe();

    this.errorOverview$
      .pipe(
        filter((error) => error != null),
        tap((error) => {
          this.errorOverview = error;
        }),
        switchMap(() =>
          this.translation.translate("customManufacturerCampaign.error")
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
        })
      )
      .subscribe();

      this.updateSuccess$
      .pipe(
        filter((success) => {
          return success && success == true;
        }),
        tap(() => {
          this.getManufacturerCampaignOverview(this.productCode,this.manufacturerNumber,this.ean,this.manufacturerId,this.campaignId,this.showOldData);
        })
      )
      .subscribe();
  }

  setSortOptions(): void {
    this.selectedSortOption =
      CustomManufacturerCampaignSortOptions[
        CustomManufacturerCampaignSortOptions.id
      ];
    this.sortOptions = Object.values(
      CustomManufacturerCampaignSortOptions
    ).filter((value) => typeof value === "string") as string[];
  }

  onSubmit() {
      this.productCode = this.searchForm.value.productCode;
      this.manufacturerNumber = this.searchForm.value.manufacturerNumber;
      this.ean = this.searchForm.value.ean;
      this.manufacturerId = this.searchForm.value.manufacturerId;
      this.campaignId = this.searchForm.value.campaignId
      this.getManufacturerCampaignOverview(this.productCode,this.manufacturerNumber,this.ean,this.manufacturerId,this.campaignId,this.showOldData);

  }

  onShowOldData() {
    this.showOldData =!this.showOldData;
    this.campaignId = '';
    this.getManufacturerCampaignOverview(this.productCode,this.manufacturerNumber,this.ean,this.manufacturerId,this.campaignId,this.showOldData);
}

  onFileSelected(event: any) {
    this.fileToBeImported = event.target.files[0];
    if (this.fileToBeImported) {
        this.fileName = this.fileToBeImported.name;
    }
  }

  importFile():void{
    if(this.fileToBeImported){
      this.manufacturerCampaignService.importFile(this.fileToBeImported);
    }
    this.fileToBeImported = null;
    this.fileName = '';
  }

  resetForm() {
    this.searchForm.resetForm({
      manufacturerNumber:"",
      ean:"",
      campaignId:"",
      manufacturerId:"",
      productCode:""
    });
    this.onSubmit();
  }

  openInstruction() {
    this.manufacturerCampaignService.openInstructions();
  }

  getManufacturerCampaignOverview(productCode?:string,manufacturerNumber?:string,ean?:string,manufacturerId?:string,campaignId?:string,showOldData?:boolean): void {
    this.overview = null;
    this.responses = null;
    this.errorOverview = null;
    this.store.dispatch(getManufacturerCampaign({
      productCode:productCode,
      manufacturerNumber:manufacturerNumber,
      manufacturerId:manufacturerId,
      ean:ean,
      campaignId:campaignId,
      showOldData:showOldData
    }));
  }

  onChangeSort(): void {
    this.responses = [
      ...this.overview.responses,
    ].sort((a, b) => {
      if (this.processNumberOrValue(this.selectedSortOption)) {
        const res = this.compare(
          parseInt(a[this.selectedSortOption]),
          parseInt(b[this.selectedSortOption])
        );
        return this.sortDirection === "asc" ? res : -res;
      } else {
        const res = this.compare(
          a[this.selectedSortOption].toUpperCase(),
          b[this.selectedSortOption].toUpperCase()
        );
        return this.sortDirection === "asc" ? res : -res;
      }
    });
  }

  processNumberOrValue(selectedSortOption: string): boolean {
    return (
      selectedSortOption ===
      String(
        CustomManufacturerCampaignSortOptions[
          CustomManufacturerCampaignSortOptions.productCode
        ]
      )
    );
  }

  openDetails(
    expired:boolean,
    campaignName: string,
    campaignId: string,
    manufacturer: string,
    productCode: string,
    productName: string,
    productEan: string,
    supplierArticleNumber: string,
    noSerialNumberCheck: boolean,
    startedValidity: boolean,
    tradeInProduct: boolean,
    numberOfCombinations?: string,
    combiProducts?: Product[],
    combiValue1?: string,
    campaignCodeInput?:boolean
    ) {
    this.manufacturerCampaignService.
      openDetails(
        expired,
        campaignName,
        campaignId,
        manufacturer,
        productCode,
        productName,
        productEan,
        supplierArticleNumber,
        noSerialNumberCheck,
        startedValidity,
        tradeInProduct,
        numberOfCombinations,
        combiProducts,
        combiValue1,
        campaignCodeInput
        );
  }

  openExport() {
    this.manufacturerCampaignService.openExport(this.responses, this.b2bUnitId);
  }

  downloadCalendar(event: any, campaignId: string) {
    event.stopPropagation();
    this.manufacturerCampaignService.downloadCampaignCalendar(campaignId).pipe(
      tap(
        (data) => {
          const link = document.createElement('a');
          link.setAttribute('download', campaignId + '.ics');
          link.href = URL.createObjectURL(data);
          link.click();
          document.body.removeChild(link);
        }
      )
    ).subscribe(() => {});
  }

  downloadOverview(){
    this.manufacturerCampaignService.downloadCsv(this.responses);
  }
}
